import { MoneyText } from '@/components/MoneyText';
import { errorCopy } from '@/api/errorCopy';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  accountsApi,
  SUBTYPES_BY_TYPE,
  type Account,
  type AccountCreateRequest,
  type AccountSubtype,
} from '@/api/accounts';
import { backendFields } from '@/api/errorDetails';
import { ErrorCodes, isApiError } from '@/api/errors';
import { compactIdentifier } from '@/features/accounts/identifierDisplay';
import { translateServerPhrase } from '@/features/shared/useFieldErrors';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { transfersApi } from '@/api/transfers';
import { ConfirmButton } from '@/components/ConfirmButton';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { unmanagedOutlineSx } from './unmanagedStyle';
import { registerNear } from './registerNear';
import { InstitutionPicker, type InstitutionValue } from './InstitutionPicker';
import { SubmitButton } from '@/components/SubmitButton';
import { toCivil } from '@/features/envelope/window';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { SUBSCRIBE_CURRENCIES } from '@/features/subscribe/currencies';

/** The picker's last entry: add the destination in place (TMMAPP-0120 item 9, TMMAPP-0121 item 4). */
const ADD_ACCOUNT = 'add-account';
/** A destination added here is a cash or a revolving account (a loan needs its terms). */
const ADD_SUBTYPES: readonly AccountSubtype[] = [
  ...SUBTYPES_BY_TYPE.CASH,
  ...SUBTYPES_BY_TYPE.REVOLVING,
];
const NAME_MAX_LENGTH = 120;

function typeOfSubtype(subtype: AccountSubtype): 'CASH' | 'REVOLVING' {
  return SUBTYPES_BY_TYPE.REVOLVING.includes(subtype) ? 'REVOLVING' : 'CASH';
}

interface TransferDestinationPanelProps {
  account: Account;
  line: OwnerRegisterLine;
  editor: boolean;
  operatingCurrency: string | null;
  onChanged: (line: OwnerRegisterLine) => void | Promise<void>;
  onNotify: (message: string) => void;
  /** The host's in-place subscribe step; `invoker` is where focus returns when it closes. */
  onSubscriptionRequired: (invoker?: HTMLElement | null) => void;
  /** Close the host dialog after a successful classify (the register chip is the confirmation). */
  onClose?: () => void;
  /** The in-place create form has typed work (the host's unsaved-changes guard, TMM-B5). */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * The transfer treatment of a line (TMMAPP-0026 / TMMAPP-0031), as the allocate dialog's
 * collapsible Transfer section (TMMAPP-0109 D1): a classified line shows its chip with Unlink /
 * Clear; an unclassified one picks the destination account and saves, or links the matching line
 * on a managed account. A line with live allocations cannot become a transfer until they are
 * removed above.
 *
 * TMMAPP-0120 item 9, TMMAPP-0121 item 4: the picker ends with "+ Add account". Choosing it swaps the
 * panel's body for a compact create form in the same dialog (never a stacked one, DLG-005) that
 * offers the add-account form's "How do you track it?" choice. Back returns to the picker unchanged,
 * and Save creates the account and comes back with it selected; the person confirms the transfer as
 * usual. An unmanaged account takes the source account's currency (an unmanaged transfer is
 * same-currency, MM-D-87); a managed one may be any currency. A non-subscriber's entry opens the
 * purchase experience instead.
 */
export function TransferDestinationPanel({
  account,
  line,
  editor,
  operatingCurrency,
  onChanged,
  onNotify,
  onSubscriptionRequired,
  onClose,
  onDirtyChange,
}: TransferDestinationPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const classified = line.transferAccountId !== null;
  const hasLiveAllocations = line.allocations.length > 0;
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [transferAccountId, setTransferAccountId] = useState<number | ''>(
    line.transferAccountId ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkLines, setLinkLines] = useState<OwnerRegisterLine[]>([]);
  const [creating, setCreating] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const returnPickerFocus = useRef(false);
  const pickerCombobox = () =>
    pickerRef.current?.querySelector<HTMLElement>('[role="combobox"]') ?? null;
  // A non-subscriber gets the host dialog's in-place subscribe card, as Save does (DLG-005).
  const createEntry = useSubscriberAction(
    () => setCreating(true),
    false,
    () => onSubscriptionRequired(pickerCombobox()),
  );
  const leaveForm = () => {
    returnPickerFocus.current = true;
    setCreating(false);
  };

  // Back or Save in the form: focus returns to the destination picker it replaced (WCAG 2.4.3).
  useEffect(() => {
    if (creating || !returnPickerFocus.current) return;
    returnPickerFocus.current = false;
    pickerRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
  }, [creating]);

  useEffect(() => {
    let cancelled = false;
    void accountsApi
      .list()
      .then((list) => {
        if (!cancelled) {
          setAccounts(list.filter((a) => a.id !== account.id && a.access === 'OWNER'));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [account.id]);

  const managed = accounts.filter((a) => a.management === 'MANAGED');
  const unmanaged = accounts.filter((a) => a.management === 'UNMANAGED');
  const chosen =
    typeof transferAccountId === 'number'
      ? (accounts.find((a) => a.id === transferAccountId) ?? null)
      : null;

  async function handleClassify() {
    if (typeof transferAccountId !== 'number' || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await transfersApi.classify(account.id, line.id, transferAccountId);
      await onChanged(updated);
      onNotify(t('transfer.chip', { account: updated.transferAccountName ?? '' }));
      // Leave the register visible with the chip — do not keep the dialog open across the
      // refetch (Escape races remounts). Clear is available by reopening the row.
      onClose?.();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else {
        setError(errorCopy(t, err, 'accounts.errorGeneric'));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    setSaving(true);
    setError(null);
    try {
      const updated = await transfersApi.clear(account.id, line.id);
      await onChanged(updated);
      setTransferAccountId('');
      onNotify(t('transfer.cleared'));
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else {
        setError(errorCopy(t, err, 'accounts.errorGeneric'));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleUnlink() {
    try {
      await transfersApi.unlink(line.id);
      const updated = await transactionsApi.get(account.id, line.id);
      await onChanged(updated);
      setTransferAccountId('');
      onNotify(t('transfer.unlink'));
    } catch (err) {
      onNotify(errorCopy(t, err, 'accounts.errorGeneric'));
    }
  }

  async function openLinkCandidates() {
    if (chosen === null || chosen.management !== 'MANAGED') return;
    setLinkOpen(true);
    try {
      const opposite = -Math.sign(line.operatingAmountMinor);
      setLinkLines(
        await registerNear(
          chosen.id,
          line.postedOn,
          (row) =>
            row.status === 'POSTED' &&
            row.transferAccountId === null &&
            Math.sign(row.operatingAmountMinor) === opposite,
        ),
      );
    } catch {
      setLinkLines([]);
    }
  }

  async function handleLink(otherId: number) {
    try {
      const result = await transfersApi.link(line.id, otherId);
      const updated = await transactionsApi.get(account.id, line.id);
      await onChanged(updated);
      if (result.differenceMinor !== null && result.differenceMinor !== 0) {
        onNotify(
          t('transfer.linkDifference', {
            amount: formatMoneyMinor(
              result.differenceMinor,
              result.currencyA || operatingCurrency,
              locale,
            ),
          }),
        );
      } else {
        onNotify(t('transfer.linked'));
      }
      setLinkOpen(false);
    } catch (err) {
      onNotify(errorCopy(t, err, 'accounts.errorGeneric'));
    }
  }

  if (!editor) {
    if (classified) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('transfer.chip', { account: line.transferAccountName ?? '' })}
        </Typography>
      );
    }
    return null;
  }

  if (classified) {
    return (
      <Stack spacing={1.5}>
        {error !== null && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2">
          {t('transfer.chip', { account: line.transferAccountName ?? '' })}
        </Typography>
        {line.transferPairTransactionId !== null && (
          <Button size="small" onClick={() => void handleUnlink()}>
            {t('transfer.unlink')}
          </Button>
        )}
        <ConfirmButton
          size="small"
          color="warning"
          onConfirm={() => void handleClear()}
          aria-description={t('transfer.clearConfirm')}
        >
          {t('transfer.clear')}
        </ConfirmButton>
      </Stack>
    );
  }

  if (creating) {
    return (
      <AddAccountForm
        source={account}
        operatingCurrency={operatingCurrency}
        onBack={leaveForm}
        onCreated={(created) => {
          setAccounts((prev) => [...prev, created]);
          setTransferAccountId(created.id);
          leaveForm();
        }}
        onDirtyChange={onDirtyChange}
        onSubscriptionRequired={onSubscriptionRequired}
      />
    );
  }

  if (hasLiveAllocations) {
    return (
      <Alert severity="warning">
        {t('transfer.hasAllocations', { count: line.allocations.length })}
      </Alert>
    );
  }

  return (
    <Stack spacing={1}>
      {error !== null && <Alert severity="error">{error}</Alert>}
      <FormControl fullWidth size="small" ref={pickerRef}>
        <InputLabel id="transfer-account-label">{t('transfer.account')}</InputLabel>
        <Select
          labelId="transfer-account-label"
          label={t('transfer.account')}
          value={transferAccountId === '' ? '' : String(transferAccountId)}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === ADD_ACCOUNT) {
              // The selection stays as it was; the entry only opens the form (or purchase).
              createEntry.run(undefined as unknown as MouseEvent<HTMLButtonElement>);
              return;
            }
            setTransferAccountId(raw === '' ? '' : Number(raw));
          }}
        >
          <ListSubheader>{t('transfer.groupManaged')}</ListSubheader>
          {managed.map((a) => (
            <MenuItem key={a.id} value={String(a.id)}>
              {a.name}
            </MenuItem>
          ))}
          {unmanaged.length > 0 && <ListSubheader>{t('transfer.groupUnmanaged')}</ListSubheader>}
          {unmanaged.map((a) => (
            <MenuItem key={a.id} value={String(a.id)} sx={unmanagedOutlineSx(tokens)}>
              {a.name}
            </MenuItem>
          ))}
          <MenuItem
            value={ADD_ACCOUNT}
            data-add-account=""
            aria-describedby={createEntry.descriptionId}
            sx={{ color: tokens.link }}
          >
            <span aria-hidden>+&nbsp;</span>
            {t('transfer.addAccount')}
          </MenuItem>
        </Select>
      </FormControl>
      <SubscriberActionHint id={createEntry.descriptionId} description={createEntry.description} />
      <Box>
        <SubmitButton
          disabled={typeof transferAccountId !== 'number'}
          loading={saving}
          onClick={() => void handleClassify()}
        >
          {t('transfer.save')}
        </SubmitButton>
      </Box>
      {chosen !== null && chosen.management === 'MANAGED' && (
        <Box>
          <Button size="small" onClick={() => void openLinkCandidates()}>
            {t('transfer.linkTitle')}
          </Button>
          {linkOpen && (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {linkLines.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <Typography variant="body2">
                    {formatCivilDate(row.postedOn, locale)} · {row.payee} ·{' '}
                    <MoneyText minor={row.operatingAmountMinor} currency={operatingCurrency} />
                  </Typography>
                  <Button size="small" onClick={() => void handleLink(row.id)}>
                    {t('transfer.link')}
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
}

/** The currencies a managed destination may take: the source account's first. */
function currencyOptions(first: string | null): string[] {
  const rest = SUBSCRIBE_CURRENCIES.filter((code) => code !== first);
  return first === null ? rest : [first, ...rest];
}

/**
 * The in-place create form for a destination (TMMAPP-0121 item 4): the add-account form's "How do
 * you track it?" choice, then the name, type, institution and last four digits. Managed is the one
 * default from every entry (TMMAPP-0139 item 7, TMMAPP-0140 item 6): it offers any currency, as the
 * add-account form does, and sends no opening. Unmanaged (0120 item 9's path) takes the source
 * account's currency and offers the net-worth switch.
 */
export function AddAccountForm({
  source,
  operatingCurrency,
  onBack,
  onCreated,
  onSubscriptionRequired,
  onDirtyChange,
  initialLast4 = '',
  initialIdentifier = '',
  initialInstitution = '',
  initialSubtype = 'SAVINGS',
  initialManagement = 'MANAGED',
  showHeading = true,
  backLabel,
}: {
  /** The line's account; null when the form isn't for one line (the missing-account card). */
  source: Account | null;
  operatingCurrency: string | null;
  onBack: () => void;
  onCreated: (account: Account) => void;
  onSubscriptionRequired: () => void;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
  /** Last four digits to start with (the missing-account card's mask, TMMAPP-0119 item 25). */
  initialLast4?: string;
  /** An IBAN, CLABE or account number to start with (TMMAPP-0133). */
  initialIdentifier?: string;
  /** The wire's bank, as the institution (TMMAPP-0133). */
  initialInstitution?: string;
  /** The type to start with: Savings for a destination, Checking from a review item (TMMAPP-0136). */
  initialSubtype?: AccountSubtype;
  /** How it is tracked to start with: managed by default (TMMAPP-0140 item 6). */
  initialManagement?: 'MANAGED' | 'UNMANAGED';
  /** False inside a dialog already titled "Add account" (TMMAPP-0136 item 2). */
  showHeading?: boolean;
  /** The way out of the form; "Back" by default. */
  backLabel?: string;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const sourceCurrency = source?.currency ?? operatingCurrency;
  const [management, setManagement] = useState<'MANAGED' | 'UNMANAGED'>(initialManagement);
  const [managedCurrency, setManagedCurrency] = useState<string>(sourceCurrency ?? '');
  const [name, setName] = useState('');
  const [subtype, setSubtype] = useState<AccountSubtype>(initialSubtype);
  const [institution, setInstitution] = useState<InstitutionValue>({
    id: null,
    name: initialInstitution,
  });
  const [last4, setLast4] = useState(initialLast4);
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [identifierError, setIdentifierError] = useState<string | null>(null);
  const [includeInNetWorth, setIncludeInNetWorth] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [last4Error, setLast4Error] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const managed = management === 'MANAGED';
  const currency = managed ? managedCurrency || null : sourceCurrency;
  const dirty =
    name.trim() !== '' ||
    institution.name.trim() !== '' ||
    last4 !== initialLast4 ||
    identifier !== initialIdentifier ||
    institution.name !== initialInstitution ||
    institution.id !== null ||
    subtype !== initialSubtype ||
    management !== initialManagement ||
    includeInNetWorth;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [onDirtyChange, dirty]);
  // Gone (saved, Back, or the panel unmounted): nothing of the form's is left unsaved.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // The form replaced the picker in place: focus starts on its heading, as an in-place step does.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  async function handleSave() {
    if (saving) return;
    const trimmed = name.trim();
    const badName = trimmed.length === 0;
    const badLast4 = last4.length > 0 && !/^\d{4}$/.test(last4);
    setNameError(badName);
    setLast4Error(badLast4);
    if (badName || badLast4) {
      document.getElementById(badName ? 'add-account-name' : 'add-account-last4')?.focus();
      return;
    }
    // The add-account form's request. A managed account sends no opening, so it is pending and set
    // from its first import, as the add-account dialog's "from my first import" does (TMMAPP-0139
    // item 7); an unmanaged one has no register and opens at zero as of today.
    const body: AccountCreateRequest = {
      type: typeOfSubtype(subtype),
      subtype,
      name: trimmed,
      ...(managed ? {} : { openingBalanceMinor: 0, openingBalanceOn: toCivil(new Date()) }),
      management,
      includeInNetWorth: managed ? false : includeInNetWorth,
    };
    if (currency !== null) body.currency = currency;
    // A pick sends its id alone; the free text is sent only when typed (TMMAPP-0165 item 17).
    if (institution.id !== null) body.institutionId = institution.id;
    else if (institution.name.trim().length > 0) body.institution = institution.name.trim();
    if (last4.length === 4) body.last4 = last4;
    const compact = compactIdentifier(identifier);
    if (compact.length > 0) body.externalIdentifier = compact;
    setSaving(true);
    setError(null);
    setIdentifierError(null);
    try {
      onCreated(await accountsApi.create(body));
    } catch (err) {
      const fields = backendFields(err);
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else if (fields.externalIdentifier !== undefined) {
        setIdentifierError(translateServerPhrase(fields.externalIdentifier, t));
      } else {
        setError(errorCopy(t, err, 'accounts.errorGeneric'));
      }
      setSaving(false);
    }
  }

  return (
    <Stack spacing={1.5} data-add-account-form="">
      {showHeading && (
        <Typography variant="subtitle2" component="h3" ref={headingRef} tabIndex={-1}>
          {t('transfer.addAccount')}
        </Typography>
      )}
      {error !== null && <Alert severity="error">{error}</Alert>}
      <FormControl>
        <FormLabel id="add-account-management">{t('accounts.trackHow')}</FormLabel>
        <RadioGroup
          aria-labelledby="add-account-management"
          value={management}
          onChange={(_event, value) => setManagement(value as 'MANAGED' | 'UNMANAGED')}
        >
          <FormControlLabel value="MANAGED" control={<Radio />} label={t('accounts.managed')} />
          <FormControlLabel value="UNMANAGED" control={<Radio />} label={t('accounts.unmanaged')} />
        </RadioGroup>
      </FormControl>
      <TextField
        id="add-account-name"
        size="small"
        label={t('accounts.nameLabel')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        fullWidth
        error={nameError}
        helperText={nameError ? t('transfer.newNameRequired') : undefined}
        slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
      />
      <FormControl fullWidth size="small">
        <InputLabel id="add-account-type">{t('accounts.typeLabel')}</InputLabel>
        <Select
          labelId="add-account-type"
          label={t('accounts.typeLabel')}
          value={subtype}
          onChange={(event) => setSubtype(event.target.value as AccountSubtype)}
        >
          {ADD_SUBTYPES.map((option) => (
            <MenuItem key={option} value={option}>
              {t(`accounts.subtype.${option}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5 }}>
        <InstitutionPicker
          size="small"
          label={t('accounts.institutionLabel')}
          value={institution}
          onChange={setInstitution}
        />
        <TextField
          id="add-account-last4"
          size="small"
          label={t('accounts.last4Label')}
          value={last4}
          onChange={(event) => setLast4(event.target.value.replace(/\D/g, '').slice(0, 4))}
          fullWidth
          error={last4Error}
          helperText={last4Error ? t('accounts.last4Help') : undefined}
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
        />
      </Stack>
      <TextField
        id="add-account-identifier"
        size="small"
        label={t('accounts.identifierLabel')}
        value={identifier}
        onChange={(event) => setIdentifier(event.target.value)}
        fullWidth
        error={identifierError !== null}
        helperText={identifierError ?? t('accounts.identifierHelp')}
        slotProps={{ htmlInput: { maxLength: 42 } }}
      />
      {managed ? (
        <FormControl fullWidth size="small">
          <InputLabel id="add-account-currency">{t('accounts.currencyLabel')}</InputLabel>
          <Select
            labelId="add-account-currency"
            label={t('accounts.currencyLabel')}
            value={managedCurrency}
            onChange={(event) => setManagedCurrency(event.target.value)}
          >
            {currencyOptions(sourceCurrency).map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={includeInNetWorth}
                onChange={(_event, checked) => setIncludeInNetWorth(checked)}
              />
            }
            label={t('accounts.includeInNetWorth')}
            sx={{ marginInlineStart: 0 }}
          />
          <Typography variant="body2" data-add-account-currency="" sx={{ color: tokens.text2 }}>
            {source !== null
              ? t('transfer.newCurrency', { code: sourceCurrency ?? '—', account: source.name })
              : t('transfer.newCurrencyPlain', { code: sourceCurrency ?? '—' })}
          </Typography>
        </>
      )}
      <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button onClick={onBack} disabled={saving}>
          {backLabel ?? t('transfer.back')}
        </Button>
        <SubmitButton loading={saving} onClick={() => void handleSave()}>
          {t('common.save')}
        </SubmitButton>
      </Stack>
    </Stack>
  );
}
